/**
 * QASection — Phase 2E agent-side Q&A thread on a learning assignment.
 */
import { useEffect, useState, useCallback } from 'react';
import { MessageCircleQuestion } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

export default function QASection({ assignmentId }) {
  const { get, post } = useApi();
  const [questions, setQuestions] = useState([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    get(`/me/learning/${assignmentId}/questions`)
      .then(r => setQuestions(r.questions || []))
      .catch(() => {});
  }, [get, assignmentId]);

  useEffect(() => { load(); }, [load]);

  const ask = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await post(`/me/learning/${assignmentId}/questions`, { text: draft.trim() });
      setDraft('');
      toast.success('Question sent to your trainer');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="mt-4" data-testid="qa-section">
      <div className="p-3 border-b flex items-center gap-2">
        <MessageCircleQuestion className="w-4 h-4 text-amber-600" />
        <h4 className="font-display text-sm font-semibold">Questions & Answers</h4>
        <span className="text-xs text-muted-foreground ml-auto">{questions.length}</span>
      </div>
      <div className="p-3 border-b space-y-2">
        <Textarea rows={2} placeholder="Stuck on something? Ask your trainer…"
                  value={draft} onChange={e => setDraft(e.target.value)}
                  data-testid="qa-input" />
        <div className="flex justify-end">
          <Button size="sm" onClick={ask} disabled={saving || !draft.trim()} data-testid="qa-ask-btn">
            {saving ? 'Sending…' : 'Ask Question'}
          </Button>
        </div>
      </div>
      {questions.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No questions yet — answers from your trainer appear here.</p>
      ) : (
        <div className="divide-y max-h-80 overflow-y-auto" data-testid="qa-list">
          {questions.map(q => (
            <div key={q.question_id} className="p-3" data-testid={`qa-${q.question_id}`}>
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{q.trainee_name}</span>
                {' '}· {new Date(q.created_at).toLocaleString()}
                {q.resolved && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-0 text-[10px]">Resolved</Badge>}
              </div>
              <p className="text-sm mt-1">{q.text}</p>
              {(q.replies || []).map(r => (
                <div key={r.reply_id} className="mt-2 ml-4 pl-3 border-l-2 border-amber-200">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{r.author_name}</span> (trainer)
                    {' '}· {new Date(r.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm mt-0.5">{r.text}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
